// src/utils/invitationUtils.js

/**
 * Utility functions for working with invitation codes and invitation links
 */

/**
 * Generate a shareable invitation link with the given code
 * @param {string} code - The invitation code
 * @param {string} path - The path to use (default: '/join')
 * @returns {string} The full invitation URL
 */
export const generateInvitationLink = (code, path = '/join') => {
  // Use the base domain from .env if available, otherwise use the current domain
  const baseDomain = process.env.REACT_APP_BASE_DOMAIN || window.location.origin;
  const url = new URL(path, baseDomain);
  url.searchParams.set('code', code);
  return url.toString();
};

/**
 * Extract an invitation code from a URL if present
 * @param {string} url - The URL to parse (defaults to current window location)
 * @returns {string|null} The invitation code, or null if none found
 */
export const extractInvitationCode = (url = window.location.href) => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.searchParams.get('code');
  } catch (err) {
    console.error('Error parsing URL:', err);
    return null;
  }
};

/**
 * Share an invitation link via the Web Share API if available
 * Falls back to copying to clipboard if Web Share API is not available
 * 
 * @param {string} invitationCode - The invitation code to share
 * @param {Function} onSuccess - Callback for successful share/copy
 * @param {Function} onError - Callback for errors
 */
export const shareInvitationLink = async (invitationCode, onSuccess, onError) => {
  if (!invitationCode) {
    if (onError) onError('No invitation code to share');
    return;
  }
  
  const shareText = `Join me on Aledger! Use my invitation code ${invitationCode} to get started.`;
  const shareUrl = generateInvitationLink(invitationCode);
  
  // Try to use the Web Share API if available
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Join Aledger',
        text: shareText,
        url: shareUrl
      });
      
      if (onSuccess) onSuccess('Shared successfully');
    } catch (err) {
      console.error('Error sharing:', err);
      
      // If user cancelled sharing, don't treat as an error
      if (err.name !== 'AbortError') {
        // Fall back to clipboard
        copyToClipboard(shareUrl, onSuccess, onError);
      }
    }
  } else {
    // Fall back to clipboard if Web Share API not available
    copyToClipboard(shareUrl, onSuccess, onError);
  }
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 */
export const copyToClipboard = async (text, onSuccess, onError) => {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed'; // Prevent scrolling to bottom
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    
    if (onSuccess) onSuccess('Copied to clipboard');
  } catch (err) {
    console.error('Failed to copy:', err);
    if (onError) onError('Failed to copy to clipboard');
  }
};

export default {
  generateInvitationLink,
  extractInvitationCode,
  shareInvitationLink,
  copyToClipboard
};
